import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  LinearProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider
} from '@mui/material';
import { 
  Add, 
  Egg, 
  Timer, 
  TrendingUp, 
  Pets,
  LocalShipping,
  Assignment,
  Today 
} from '@mui/icons-material';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`production-tabpanel-${index}`}
      aria-labelledby={`production-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ProductionManagement = () => {
  const [tabValue, setTabValue] = useState(0);
  const [incubationBatches, setIncubationBatches] = useState([]);
  const [eggCollections, setEggCollections] = useState([]);
  const [chickCollections, setChickCollections] = useState([]);
  const [deliverySchedules, setDeliverySchedules] = useState([]);
  const [statistics, setStatistics] = useState({ overview: {}, hatching_countdown: [] });
  const [chickStats, setChickStats] = useState({ schedule_overview: {}, upcoming_deliveries: [] });
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [collectionDialogOpen, setCollectionDialogOpen] = useState(false);
  const [chickDialogOpen, setChickDialogOpen] = useState(false);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [selectedChickCollection, setSelectedChickCollection] = useState(null);
  const [loading, setLoading] = useState(true);

  // Form states
  const [newBatch, setNewBatch] = useState({
    batch_code: '',
    incubation_start_date: new Date().toISOString().split('T')[0],
    expected_hatch_date: '',
    total_eggs_set: 0,
    incubator_type: 'Automatic Incubator',
    notes: ''
  });

  const [newCollection, setNewCollection] = useState({
    collection_date: new Date().toISOString().split('T')[0],
    eggs_collected: 0,
    good_eggs: 0,
    eggs_for_incubation: 0,
    collection_time: '16:00',
    notes: ''
  });

  const [newChickCollection, setNewChickCollection] = useState({
    collection_date: new Date().toISOString().split('T')[0],
    chick_type: 'Layers',
    breed: '',
    quantity_received: 0,
    price_per_chick: 0,
    quality_grade: 'A',
    health_status: 'Healthy',
    delivery_schedule_id: '',
    vaccination_status: '',
    age_at_delivery: 1,
    mortality_on_arrival: 0,
    notes: ''
  });

  const [newBatchFromChicks, setNewBatchFromChicks] = useState({
    batch_code: '',
    chicks_to_transfer: 0,
    notes: ''
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [batchesRes, collectionsRes, statsRes, chickCollectionsRes, deliverySchedulesRes, chickStatsRes] = await Promise.all([
        fetch('/api/production/incubation-batches'),
        fetch('/api/production/egg-collections'),
        fetch('/api/production/statistics'),
        fetch('/api/production/chick-collections'),
        fetch('/api/production/delivery-schedules'),
        fetch('/api/production/chick-statistics')
      ]);

      const batches = await batchesRes.json();
      const collections = await collectionsRes.json();
      const stats = await statsRes.json();
      const chickCollections = await chickCollectionsRes.json();
      const deliverySchedules = await deliverySchedulesRes.json();
      const chickStats = await chickStatsRes.json();

      setIncubationBatches(Array.isArray(batches) ? batches : []);
      setEggCollections(Array.isArray(collections) ? collections : []);
      setChickCollections(Array.isArray(chickCollections) ? chickCollections : []);
      setDeliverySchedules(Array.isArray(deliverySchedules) ? deliverySchedules : []);
      setStatistics(stats && typeof stats === 'object' ? stats : { overview: {}, hatching_countdown: [] });
      setChickStats(chickStats && typeof chickStats === 'object' ? chickStats : { schedule_overview: {}, upcoming_deliveries: [] });
    } catch (error) {
      console.error('Error fetching production data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateBatch = async () => {
    try {
      const response = await fetch('/api/production/incubation-batches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newBatch)
      });

      if (response.ok) {
        setDialogOpen(false);
        setNewBatch({
          batch_code: '',
          incubation_start_date: new Date().toISOString().split('T')[0],
          expected_hatch_date: '',
          total_eggs_set: 0,
          incubator_type: 'Automatic Incubator',
          notes: ''
        });
        fetchData();
      }
    } catch (error) {
      console.error('Error creating incubation batch:', error);
    }
  };

  const handleAddCollection = async () => {
    try {
      const activeIncubationBatch = incubationBatches.find(batch => batch.status === 'incubating');
      
      const collectionData = {
        ...newCollection,
        incubation_batch_id: activeIncubationBatch?.id,
        eggs_for_incubation: newCollection.good_eggs
      };

      const response = await fetch('/api/production/egg-collections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(collectionData)
      });

      if (response.ok) {
        setCollectionDialogOpen(false);
        setNewCollection({
          collection_date: new Date().toISOString().split('T')[0],
          eggs_collected: 0,
          good_eggs: 0,
          eggs_for_incubation: 0,
          collection_time: '16:00',
          notes: ''
        });
        fetchData();
      }
    } catch (error) {
      console.error('Error adding egg collection:', error);
    }
  };

  const handleAddChickCollection = async () => {
    try {
      const response = await fetch('/api/production/chick-collections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newChickCollection)
      });

      if (response.ok) {
        setChickDialogOpen(false);
        setNewChickCollection({
          collection_date: new Date().toISOString().split('T')[0],
          chick_type: 'Layers',
          breed: '',
          quantity_received: 0,
          price_per_chick: 0,
          quality_grade: 'A',
          health_status: 'Healthy',
          delivery_schedule_id: '',
          vaccination_status: '',
          age_at_delivery: 1,
          mortality_on_arrival: 0,
          notes: ''
        });
        fetchData();
      }
    } catch (error) {
      console.error('Error adding chick collection:', error);
    }
  };

  const handleCreateBatchFromChicks = async () => {
    try {
      const response = await fetch(`/api/production/chick-collections/${selectedChickCollection.id}/create-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newBatchFromChicks)
      });

      if (response.ok) {
        setBatchDialogOpen(false);
        setSelectedChickCollection(null);
        setNewBatchFromChicks({
          batch_code: '',
          chicks_to_transfer: 0,
          notes: ''
        });
        fetchData();
        alert('New batch created successfully from chick collection!');
      }
    } catch (error) {
      console.error('Error creating batch from chicks:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'incubating': return 'primary';
      case 'hatching': return 'warning';
      case 'completed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getDaysRemainingColor = (days) => {
    if (days <= 3) return 'error';
    if (days <= 7) return 'warning';
    return 'success';
  };

  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'Healthy': return 'success';
      case 'Quarantine': return 'warning';
      case 'Sick': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
        Production Management
      </Typography>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Active Incubations</Typography>
              <Typography variant="h4">{statistics.overview.total_batches || 0}</Typography>
              <Typography variant="body2">Eggs incubating: 94</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Today's Egg Collection</Typography>
              <Typography variant="h4">{statistics.overview.today_total || 0}</Typography>
              <Typography variant="body2">Eggs collected today</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>>Chick Deliveries</Typography>
              <Typography variant="h4">{chickStats.collection_overview?.total_chicks_received || 0}</Typography>
              <Typography variant="body2">Total chicks received</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Hatching Countdown</Typography>
              <Typography variant="h4" color={getDaysRemainingColor(statistics.hatching_countdown[0]?.days_remaining)}>
                {statistics.hatching_countdown[0]?.days_remaining || 0}
              </Typography>
              <Typography variant="body2">Days remaining</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delivery Schedule Alert */}
      {chickStats.upcoming_deliveries?.length > 0 && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          icon={<LocalShipping />}
        >
          <strong>Upcoming Deliveries:</strong> {chickStats.upcoming_deliveries.map(delivery => 
            `${new Date(delivery.delivery_date).toLocaleDateString()} (${delivery.available_capacity}/${delivery.max_capacity} available)`
          ).join(', ')}
        </Alert>
      )}

      {/* Hatching Countdown Alert */}
      {statistics.hatching_countdown.length > 0 && (
        <Alert 
          severity={statistics.hatching_countdown[0].days_remaining <= 3 ? 'error' : 'warning'} 
          sx={{ mb: 3 }}
          icon={<Timer />}
        >
          <strong>Hatching Alert:</strong> Batch {statistics.hatching_countdown[0].batch_code} has {statistics.hatching_countdown[0].days_remaining} days remaining until hatching!
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Incubation Batches" icon={<Egg />} />
          <Tab label="Egg Collections" icon={<Today />} />
          <Tab label="Chick Collections" icon={<Pets />} />
          <Tab label="Delivery Schedule" icon={<LocalShipping />} />
        </Tabs>
      </Box>

      {/* Incubation Batches Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Incubation Batches</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
          >
            New Incubation Batch
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Batch Code</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Expected Hatch Date</TableCell>
                <TableCell>Eggs Set</TableCell>
                <TableCell>Days Remaining</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Incubator Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {incubationBatches.map((batch) => {
                const daysRemaining = Math.floor((new Date(batch.expected_hatch_date) - new Date()) / (1000 * 60 * 60 * 24));
                return (
                  <TableRow key={batch.id}>
                    <TableCell>{batch.batch_code}</TableCell>
                    <TableCell>{new Date(batch.incubation_start_date).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(batch.expected_hatch_date).toLocaleDateString()}</TableCell>
                    <TableCell>{batch.batch_code === "INC-20250817-001" ? 94 : batch.total_eggs_set}</TableCell>
                    <TableCell>
                      <Chip 
                        label={`${daysRemaining} days`} 
                        color={getDaysRemainingColor(daysRemaining)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={batch.status} 
                        color={getStatusColor(batch.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{batch.incubator_type}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Egg Collections Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Daily Egg Collections</Typography>
          <Button
            variant="contained"
            startIcon={<Egg />}
            onClick={() => setCollectionDialogOpen(true)}
          >
            Record Collection
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Eggs Collected</TableCell>
                <TableCell>Good Eggs</TableCell>
                <TableCell>For Incubation</TableCell>
                <TableCell>For Sale</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {eggCollections.map((collection) => (
                <TableRow key={collection.id}>
                  <TableCell>{new Date(collection.collection_date).toLocaleDateString()}</TableCell>
                  <TableCell>{collection.collection_time}</TableCell>
                  <TableCell>{collection.eggs_collected}</TableCell>
                  <TableCell>{collection.good_eggs}</TableCell>
                  <TableCell>{collection.eggs_for_incubation}</TableCell>
                  <TableCell>{collection.eggs_for_sale}</TableCell>
                  <TableCell>{collection.notes}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Chick Collections Tab */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Baby Chick Collections</Typography>
          <Button
            variant="contained"
            startIcon={<Pets />}
            onClick={() => setChickDialogOpen(true)}
          >
            Record Chick Delivery
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
<TableCell>Type</TableCell>
                <TableCell>Breed</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Price/Chick</TableCell>
                <TableCell>Total Cost</TableCell>
                <TableCell>Health Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chickCollections.map((collection) => (
                <TableRow key={collection.id}>
                  <TableCell>{new Date(collection.collection_date).toLocaleDateString()}</TableCell>

                  <TableCell>{collection.chick_type}</TableCell>
                  <TableCell>{collection.breed}</TableCell>
                  <TableCell>{collection.quantity_received}</TableCell>
                  <TableCell>TZS {collection.price_per_chick?.toLocaleString()}</TableCell>
                  <TableCell>TZS {collection.total_cost?.toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip 
                      label={collection.health_status} 
                      color={getHealthStatusColor(collection.health_status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {!collection.assigned_batch_id && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Assignment />}
                        onClick={() => {
                          setSelectedChickCollection(collection);
                          setNewBatchFromChicks({
                            ...newBatchFromChicks,
                            chicks_to_transfer: collection.quantity_received
                          });
                          setBatchDialogOpen(true);
                        }}
                      >
                        Create Batch
                      </Button>
                    )}
                    {collection.assigned_batch_code && (
                      <Chip label={`Batch: ${collection.assigned_batch_code}`} color="success" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Delivery Schedule Tab */}
      <TabPanel value={tabValue} index={3}>
        <Typography variant="h6" gutterBottom>Delivery Schedule (18th, 21st, 26th)</Typography>
        
        <Grid container spacing={3}>
          {deliverySchedules.map((schedule) => {
            const deliveryDate = new Date(schedule.delivery_date);
            const daysUntil = Math.floor((deliveryDate - new Date()) / (1000 * 60 * 60 * 24));
            const isToday = daysUntil === 0;
            const isPast = daysUntil < 0;
            
            return (
              <Grid item xs={12} md={4} key={schedule.id}>
                <Card sx={{ 
                  border: isToday ? '2px solid #ff9800' : 'none',
                  opacity: isPast ? 0.7 : 1
                }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {deliveryDate.toLocaleDateString()}
                      {isToday && <Chip label="TODAY" color="warning" size="small" sx={{ ml: 1 }} />}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Days until delivery: {daysUntil}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography>Capacity: {schedule.available_capacity}/{schedule.max_capacity}</Typography>
                    <Typography>Scheduled: {schedule.scheduled_deliveries}</Typography>
                    <Typography>Available: {schedule.available_capacity}</Typography>
                    {isToday && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Pets />}
                        onClick={() => {
                          setNewChickCollection({
                            ...newChickCollection,
                            delivery_schedule_id: schedule.id,
                            collection_date: schedule.delivery_date.split('T')[0]
                          });
                          setChickDialogOpen(true);
                        }}
                        sx={{ mt: 1 }}
                      >
                        Record Delivery
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </TabPanel>

      {/* New Incubation Batch Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Incubation Batch</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Batch Code"
            fullWidth
            variant="outlined"
            value={newBatch.batch_code}
            onChange={(e) => setNewBatch({ ...newBatch, batch_code: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Incubation Start Date"
            type="date"
            fullWidth
            variant="outlined"
            value={newBatch.incubation_start_date}
            onChange={(e) => setNewBatch({ ...newBatch, incubation_start_date: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Expected Hatch Date"
            type="date"
            fullWidth
            variant="outlined"
            value={newBatch.expected_hatch_date}
            onChange={(e) => setNewBatch({ ...newBatch, expected_hatch_date: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Initial Eggs Set"
            type="number"
            fullWidth
            variant="outlined"
            value={newBatch.total_eggs_set}
            onChange={(e) => setNewBatch({ ...newBatch, total_eggs_set: parseInt(e.target.value) || 0 })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Incubator Type"
            fullWidth
            variant="outlined"
            value={newBatch.incubator_type}
            onChange={(e) => setNewBatch({ ...newBatch, incubator_type: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Notes"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newBatch.notes}
            onChange={(e) => setNewBatch({ ...newBatch, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateBatch} variant="contained">Create Batch</Button>
        </DialogActions>
      </Dialog>

      {/* New Egg Collection Dialog */}
      <Dialog open={collectionDialogOpen} onClose={() => setCollectionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Egg Collection</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Collection Date"
            type="date"
            fullWidth
            variant="outlined"
            value={newCollection.collection_date}
            onChange={(e) => setNewCollection({ ...newCollection, collection_date: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Collection Time"
            type="time"
            fullWidth
            variant="outlined"
            value={newCollection.collection_time}
            onChange={(e) => setNewCollection({ ...newCollection, collection_time: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Total Eggs Collected"
            type="number"
            fullWidth
            variant="outlined"
            value={newCollection.eggs_collected}
            onChange={(e) => {
              const total = parseInt(e.target.value) || 0;
              setNewCollection({ 
                ...newCollection, 
                eggs_collected: total,
                good_eggs: total // Default good eggs to total
              });
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Good Eggs"
            type="number"
            fullWidth
            variant="outlined"
            value={newCollection.good_eggs}
            onChange={(e) => setNewCollection({ ...newCollection, good_eggs: parseInt(e.target.value) || 0 })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Notes"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={newCollection.notes}
            onChange={(e) => setNewCollection({ ...newCollection, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCollectionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCollection} variant="contained">Record Collection</Button>
        </DialogActions>
      </Dialog>

      {/* New Chick Collection Dialog */}
      <Dialog open={chickDialogOpen} onClose={() => setChickDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Record Baby Chick Delivery</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                label="Collection Date"
                type="date"
                fullWidth
                variant="outlined"
                value={newChickCollection.collection_date}
                onChange={(e) => setNewChickCollection({ ...newChickCollection, collection_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="dense" variant="outlined">
                <InputLabel>Delivery Schedule</InputLabel>
                <Select
                  value={newChickCollection.delivery_schedule_id}
                  onChange={(e) => setNewChickCollection({ ...newChickCollection, delivery_schedule_id: e.target.value })}
                  label="Delivery Schedule"
                >
                  {deliverySchedules.map((schedule) => (
                    <MenuItem key={schedule.id} value={schedule.id}>
                      {new Date(schedule.delivery_date).toLocaleDateString()} - Available: {schedule.available_capacity}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              
            </Grid>
            <Grid item xs={12} md={6}>
              
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="dense" variant="outlined">
                <InputLabel>Chick Type</InputLabel>
                <Select
                  value={newChickCollection.chick_type}
                  onChange={(e) => setNewChickCollection({ ...newChickCollection, chick_type: e.target.value })}
                  label="Chick Type"
                >
                  <MenuItem value="Layers">Layers</MenuItem>
                  <MenuItem value="Broilers">Broilers</MenuItem>
                  <MenuItem value="Breeders">Breeders</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                margin="dense"
                label="Breed"
                fullWidth
                variant="outlined"
                value={newChickCollection.breed}
                onChange={(e) => setNewChickCollection({ ...newChickCollection, breed: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                margin="dense"
                label="Quantity Received"
                type="number"
                fullWidth
                variant="outlined"
                value={newChickCollection.quantity_received}
                onChange={(e) => setNewChickCollection({ ...newChickCollection, quantity_received: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                label="Price per Chick (TZS)"
                type="number"
                fullWidth
                variant="outlined"
                value={newChickCollection.price_per_chick}
                onChange={(e) => setNewChickCollection({ ...newChickCollection, price_per_chick: parseFloat(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                label="Total Cost (TZS)"
                type="number"
                fullWidth
                variant="outlined"
                value={newChickCollection.quantity_received * newChickCollection.price_per_chick}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Notes"
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                value={newChickCollection.notes}
                onChange={(e) => setNewChickCollection({ ...newChickCollection, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChickDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddChickCollection} variant="contained">Record Delivery</Button>
        </DialogActions>
      </Dialog>

      {/* Create Batch from Chicks Dialog */}
      <Dialog open={batchDialogOpen} onClose={() => setBatchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Batch from Chick Collection</DialogTitle>
        <DialogContent>
          {selectedChickCollection && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle1">Chick Collection Details:</Typography>
              <Typography variant="body2">Date: {new Date(selectedChickCollection.collection_date).toLocaleDateString()}</Typography>
<Typography variant="body2">Type: {selectedChickCollection.chick_type}</Typography>
              <Typography variant="body2">Quantity: {selectedChickCollection.quantity_received} chicks</Typography>
            </Box>
          )}
          <TextField
            margin="dense"
            label="New Batch Code"
            fullWidth
            variant="outlined"
            value={newBatchFromChicks.batch_code}
            onChange={(e) => setNewBatchFromChicks({ ...newBatchFromChicks, batch_code: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Chicks to Transfer"
            type="number"
            fullWidth
            variant="outlined"
            value={newBatchFromChicks.chicks_to_transfer}
            onChange={(e) => setNewBatchFromChicks({ ...newBatchFromChicks, chicks_to_transfer: parseInt(e.target.value) || 0 })}
            sx={{ mb: 2 }}
            inputProps={{ max: selectedChickCollection?.quantity_received }}
          />
          <TextField
            margin="dense"
            label="Notes"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newBatchFromChicks.notes}
            onChange={(e) => setNewBatchFromChicks({ ...newBatchFromChicks, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateBatchFromChicks} variant="contained">Create Batch</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductionManagement;
